import React, { useState } from 'react';
import * as Sentry from '@sentry/nextjs';
import { useQuery, useMutation } from '@apollo/client';
import toast from 'react-hot-toast';
import { useRecoilState, useSetRecoilState } from 'recoil';
import { Formik, Form } from 'formik';

import {
    onCompletedHandler,
    onErrorHandler,
} from '@/services/apollo';
import Loader from '@/components/commons/loader';
import StandardModal from '@/components/commons/StandardModal';
import { FETCH_CALENDAR_INSTANCES, UPDATE_CALENDAR_INSTANCES } from '@/components/accounts/Actions';
import {
    UpdateCalendarInstanceInput, calendarInstancesAtom, onboardingAtom, userAtom,
} from '@/components/with_auth/State';
import ellipsisWrapper from '@/services/ellipsisWrapper';
import Title from '@/components/commons/page-elements/Title';
import Button, { ButtonSize, ButtonType } from '@/components/commons/button';
import { UPDATE_USER } from '@/components/onboarding/Actions';
import Heading from '@/components/commons/page-elements/Heading';

import { MAX_STRING_LENGTH } from './Constants';
import CalendarCard from './CalendarCard';

function OnboardingCalendarsModal() {
    const [open, setOpen] = useState(true);
    const [isError, setIsError] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [calendarInstances, setCalendarInstances] = useRecoilState(calendarInstancesAtom);
    const [onboardingData, setOnboardingData] = useRecoilState(onboardingAtom);
    const setUserRecoil = useSetRecoilState(userAtom);

    const [updateCalendarInstances] = useMutation(UPDATE_CALENDAR_INSTANCES);
    const [updateUser] = useMutation(UPDATE_USER);

    const setCalendarInstanceInfo = (result) => {
        const instances = result.user ? result.user.calendarInstances : result.calendarInstances;

        const calendarInstancesData = instances.map((calendarInstance) => ({
            id: calendarInstance.id,
            label: calendarInstance.label,
            effectiveLabel: calendarInstance.effectiveLabel,
            visible: calendarInstance.visible,
            writable: calendarInstance.writable,
            categories: calendarInstance.categories,
        }));
        setCalendarInstances(calendarInstancesData);
        setIsLoading(false);
    };

    useQuery(FETCH_CALENDAR_INSTANCES, {
        variables: {
            id: null,
        },
        notifyOnNetworkStatusChange: true,
        fetchPolicy: 'no-cache',
        onError: onErrorHandler(setIsError),
        onCompleted: onCompletedHandler(setIsError, setCalendarInstanceInfo),
    });

    const handleFormSubmit = async (values) => {
        const payloadItems: {
            [index: string]: UpdateCalendarInstanceInput,
        } = {};
        Object.keys(values).forEach((key) => {
            try {
                const [name, id] = key.split('-');
                if (!payloadItems[id]) {
                    payloadItems[id] = {
                        id,
                        attributes: {},
                    };
                }
                payloadItems[id].attributes[name] = values[key];
            } catch (_err) {
                // eslint-disable-next-line no-console
                console.error(`Failed to split key '${key}'. Skipping`);
            }
        });
        const requests: Array<UpdateCalendarInstanceInput> = Object.values(payloadItems);
        // TODO(tom) Potentially apollo links might work to avoid doing multiple update requests
        // but need more time to investigate, based on investigation useMutation with dynamic
        // number of requests is not recommended.

        const reqIds = requests.map((req) => req.id);

        try {
            await updateCalendarInstances({
                variables: {
                    updateCalendarInstancesInput: {
                        ids: reqIds,
                        attributes: requests.map((item) => item.attributes),
                    },
                },
            });

            const response = await updateUser({
                variables: {
                    updateUserInput: {
                        attributes: {
                            hasReviewedCalendars: true,
                        },
                    },
                },
            });
            // Do we care if this response failed to show errors? The users can't
            // action anything and they wouldn't know why it was failing in the backend.
            if (response.data.updateUser.user) {
                const { user: newUser } = response.data.updateUser;
                // update recoil so user doesn't see this page again if they navigate
                // away and back without a hard refresh
                setUserRecoil(newUser);
            }
        } catch (err) {
            const msg = 'Failed to save a Calendar';
            toast.error(msg);
            Sentry.captureException(err);
        }
        // }
        setOpen(false);
        setOnboardingData({
            ...onboardingData,
            agenda: 2,
        });
    };

    const header = (text) => {
        const smallText = text.length >= MAX_STRING_LENGTH && window.innerWidth < 360;
        const textStyle = `text-center font-bold ${smallText ? 'text-sm' : 'text-lg'}`;
        return (
            <Heading externalStyles="flex flex-col items-center w-full">
                <img src="/images/icon-calendar-weekly.svg" alt="icon-calendar" className="pb-6 w-auto" />
                <p className={textStyle}>{ellipsisWrapper(text, MAX_STRING_LENGTH)}</p>
            </Heading>
        );
    };

    // TODO(tom) I think this can be more efficient because for some reason
    // the Formik runs the initialValues BEFORE our network request finishes
    // even tho it shouldn't even be rendered yet, but if we can figure out how
    // to run this AFTER, then formik should only set values for inputs that have
    // changed and it'd save us network requests.
    const getInitialValues = () => {
        const values = {};
        calendarInstances.forEach((item) => {
            values[`categories-${item.id}`] = item.categories;
            values[`visible-${item.id}`] = item.visible;
        });
        return values;
    };

    return (
        <StandardModal
            open={open}
            onClose={() => {
                setOpen(false);
                setOnboardingData({
                    ...onboardingData,
                    agenda: 2,
                });
            }}
            classNames={{
                modal: 'p-4 sm:p-10 max-w-none max-h-[90vh]',
                closeButton: 'modal--closeButton',
            }}
        >
            <Loader isLoading={isLoading} isError={isError}>
                <Formik
                    onSubmit={handleFormSubmit}
                    initialValues={getInitialValues()}
                >
                    {({ isSubmitting }) => (
                        <Form>
                            <div className="flex flex-col justify-between items-center sm:flex-row">
                                <Title
                                    title="Let's review your calendars."
                                    subtitle="Help us understand which calendars to enable, and what type of calendars they are."
                                    externalStyles="mb-10"
                                >
                                    <Button
                                        type={ButtonType.submit}
                                        size={ButtonSize.base}
                                        text="Continue"
                                        disabled={isSubmitting}
                                    />
                                </Title>
                            </div>
                            <div className="flex flex-col gap-4 items-center md:grid md:grid-cols-2 xl:grid-cols-3">
                                {calendarInstances.map(({
                                    id, effectiveLabel, categories, visible,
                                }) => (
                                    <CalendarCard
                                        key={id}
                                        id={id}
                                        header={header(effectiveLabel)}
                                        categories={categories}
                                        visible={visible}
                                        externalStyles="flex flex-col p-6 gap-6 items-center sm:gap-8 sm:p-8 w-full max-w-[360px]"
                                    />
                                ))}
                            </div>
                        </Form>
                    )}
                </Formik>
            </Loader>

        </StandardModal>
    );
}

export default OnboardingCalendarsModal;
